import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectCreditCard } from 'src/app/Budget/redux/budget.selectors'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import Collapsible from 'src/components/Collapsible/Collapsible'
import { CreateNewDropdownOption } from 'src/components/CreateNewDropdown/CreateNewDropdown'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import MoreDropdown, {
  MoreDropdownDesigns,
} from 'src/components/MoreDropdown/MoreDropdown'
import SvgChevronDownLoD from 'src/components/Svgs/SvgChevronDownLoD'
import SvgChevronUpLoD from 'src/components/Svgs/SvgChevronUpLoD'
import SvgMoreActive from 'src/components/Svgs/SvgMoreActive'
import { useIsScrolledPast } from 'src/hooks'
import { translateSingleString } from 'src/localization/Trns'
import { createSetIsSidenavOpen } from 'src/redux/actions/app.actions'
import {
  selectIsSidenavLockedOpenFromState,
  selectShowSidenavLargeFromState,
} from 'src/redux/selectors/app.selectors'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectFixedElementPushdown } from 'src/redux/selectors/css.selectors'
import { css } from 'styled-components'

interface TopnavProps {
  mainActionOptions?: {
    topActionOptions?: CreateNewDropdownOption[]
    bottomActionOptions?: CreateNewDropdownOption[]
  }
  alternativeActionOptions?: TopnavAlternativeActionOptions[]
  titleElements?: {
    title?: string | React.ReactElement
    scrollContent?: React.ReactElement
    canRequestVirtualCardUpdateForDetailTopnav?: boolean
  }
  hideHamburger?: boolean
}

export interface TopnavAlternativeActionOptions {
  title: React.ReactNode | string
  titleActions?: {
    dispatch: () => void
    ariaLabelSource: string
    imageSrc?: string
  }
  positiveActionOptions?: CreateNewDropdownOption[]
  negativeActionOptions?: CreateNewDropdownOption[]
}

const Topnav: React.FC<TopnavProps> = (props): React.ReactNode => {
  const dispatch = useDispatch()
  const fixedElementPushdown = useSelector(selectFixedElementPushdown)
  const { analytics } = useAnalyticsContext()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const showSidenavLarge = useSelector(selectShowSidenavLargeFromState)
  const creditCard = useSelector(selectCreditCard)
  const sideNavLockedOpen = useSelector(selectIsSidenavLockedOpenFromState)

  const rightShift = sideNavLockedOpen ? '208px' : '40px'

  const { isScrolledPast } = useIsScrolledPast(120)

  const onClick = (): void => {
    dispatch(createSetIsSidenavOpen(true))
  }

  return (
    <>
      <div
        css={css`
          top: ${fixedElementPushdown + 72}px;
          z-index: 1;
          position: fixed;
          right: 0;
          height: 80px;
          width: calc(100% - ${rightShift});
          box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.15);
          ${getBreakpointDown(breakpointSizes.XSM)(css`
            height: 58px;
            width: 100%;
          `)}
        `}
      />
      <div
        css={css`
          min-height: 80px;
          max-height: 80px;
          ${getBreakpointDown(breakpointSizes.XSM)(css`
            height: 58px;
          `)}
        `}
      />
      <div
        css={css`
          top: ${fixedElementPushdown + 72}px;
          z-index: 4;
          position: fixed;
          background-color: ${styleConstants.colors.white};
          height: 80px;
          transition: width 200ms ease-out;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition:
            margin 50ms ease-out,
            padding 50ms ease-out,
            border-radius 50ms ease-out,
            width 50ms ease-out;
          width: calc(100% - ${rightShift});
          margin: 0px 80px 0px 0px;
          padding: 18px ${styleConstants.widths.headerPaddingXLGUp};
          border-radius: 0px;

          ${getBreakpointDown(breakpointSizes.XSM)(css`
            height: 58px;
            width: 100%;
            margin: 0;
            padding: 0px;
          `)}

          ${getBreakpointOnly(breakpointSizes.SM)(css`
            padding: 18px ${styleConstants.widths.headerPaddingSMOnly};
          `)}

          ${getBreakpointOnly(breakpointSizes.MD)(css`
            padding: 18px ${styleConstants.widths.headerPaddingMDOnly};
          `)}

          ${getBreakpointOnly(breakpointSizes.LG)(css`
            padding: 18px ${styleConstants.widths.headerPaddingLGOnly};
          `)}

          ${!showSidenavLarge
            ? css`
                width: 100%;
              `
            : css``}
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            min-width: 0;
          `}
        >
          {!props.hideHamburger && (
            <div
              css={css`
                display: none;
                width: 58px;
                height: 58px;
                cursor: pointer;

                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                `)}

                ${!showSidenavLarge &&
                css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                `}
              `}
              onClick={onClick}
              onKeyPress={onClick}
              role="button"
              tabIndex={0}
            >
              <SvgMoreActive />
            </div>
          )}
          {props.titleElements !== undefined && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                min-width: 0;
                flex-shrink: 2;
              `}
            >
              {props.titleElements.title !== undefined && (
                <span
                  css={css`
                    ${getFontCss(
                      14,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  {props.titleElements.title}
                </span>
              )}
              <Collapsible
                isOpen={isScrolledPast}
                css={css`
                  white-space: nowrap;
                `}
              >
                {props.titleElements.scrollContent}
              </Collapsible>
            </div>
          )}
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
            height: 100%;
            gap: 20px;

            ${getBreakpointDown(breakpointSizes.XSM)(css`
              margin-right: 12px;
            `)}
          `}
        >
          {props.alternativeActionOptions !== undefined &&
            props.alternativeActionOptions.map(
              (alternativeActionOption: TopnavAlternativeActionOptions) => (
                <>
                  {((alternativeActionOption.positiveActionOptions !==
                    undefined &&
                    alternativeActionOption.positiveActionOptions.length > 0) ||
                    (alternativeActionOption.negativeActionOptions !==
                      undefined &&
                      alternativeActionOption.negativeActionOptions.length >
                        0)) && (
                    <div
                      css={css`
                        z-index: 4;
                        ${getBreakpointDown(breakpointSizes.XSM)(css`
                          margin-right: 0px;
                          ${getFontCss(
                            10,
                            styleConstants.colors.white,
                            styleConstants.fontFamilies.medium,
                          )};
                        `)}
                      `}
                      onMouseEnter={(): void => {
                        analytics?.track('budget detail dropdown menu', {
                          budget_id:
                            creditCard !== undefined ? creditCard?.id : '',
                          issuer:
                            creditCard !== undefined ? creditCard?.issuer : '',
                        })
                      }}
                    >
                      <MoreDropdown
                        design={MoreDropdownDesigns.CtaBlueWhiteBg}
                        topOptions={
                          alternativeActionOption.positiveActionOptions || []
                        }
                        bottomOptions={
                          alternativeActionOption.negativeActionOptions || []
                        }
                        descriptionTextComponent={
                          <HoverableLink
                            css={css`
                              margin-right: 5px;
                              ${getBreakpointDown(breakpointSizes.XSM)(css`
                                ${getFontCss(
                                  12,
                                  themeConstants.colors.font_primary,
                                  styleConstants.fontFamilies.regular,
                                )};

                                &:hover {
                                  ${getFontCss(
                                    12,
                                    themeConstants.colors.font_primary,
                                    styleConstants.fontFamilies.medium,
                                  )};
                                }
                              `)}
                            `}
                            ariaLabelSource={translateSingleString(
                              locale,
                              'more dropdown',
                            )}
                          >
                            {alternativeActionOption.title}
                          </HoverableLink>
                        }
                        openComponent={<SvgChevronUpLoD />}
                        closedComponent={<SvgChevronDownLoD />}
                        minWidth={250}
                      />
                    </div>
                  )}
                  {alternativeActionOption.titleActions !== undefined && (
                    <HoverableLink
                      onClick={alternativeActionOption.titleActions.dispatch}
                      ariaLabel={
                        alternativeActionOption.titleActions.ariaLabelSource
                      }
                      colorSvgOnHover
                      css={css`
                        ${getFontCss(
                          14,
                          themeConstants.colors.cta,
                          styleConstants.fontFamilies.medium,
                        )};
                        display: flex;
                        gap: 4px;
                        justify-content: center;
                        align-items: center;
                        white-space: normal;
                        padding-bottom: 5px;
                      `}
                    >
                      <img
                        src={alternativeActionOption.titleActions.imageSrc}
                        alt={''}
                      />
                      {alternativeActionOption.title}
                    </HoverableLink>
                  )}
                </>
              ),
            )}
          {/* {(
            (props.alternativeActionOptions.positiveActionOptions !== undefined && props.alternativeActionOptions.positiveActionOptions.length > 0) ||
            (props.alternativeActionOptions.negativeActionOptions !== undefined && props.alternativeActionOptions.negativeActionOptions.length > 0)
          ) && (
            <div css={css`
              z-index: 4;
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                margin-right: 0px;
                ${getFontCss(10, styleConstants.colors.white, styleConstants.fontFamilies.medium)};
              `)}
            `}
            onMouseEnter={(): void => {
              analytics?.track('budget detail dropdown menu', {
                budget_id: creditCard !== undefined ? creditCard?.id : '',
                issuer: creditCard !== undefined ? creditCard?.issuer : '',
              })
            }}>
              <MoreDropdown
                design={MoreDropdownDesigns.CtaBlueWhiteBg}
                topOptions={props.alternativeActionOptions.positiveActionOptions || []}
                bottomOptions={props.alternativeActionOptions.negativeActionOptions || []}
                descriptionTextComponent={
                  <HoverableLink
                    css={css`
                      margin-right: 5px;
                      ${getBreakpointDown(breakpointSizes.XSM)(css`
                        ${getFontCss(12, themeConstants.colors.font_primary, styleConstants.fontFamilies.regular)};

                        &:hover{
                          ${getFontCss(12, themeConstants.colors.font_primary, styleConstants.fontFamilies.medium)};
                        }
                      `)}
                    `}
                    ariaLabelSource={translateSingleString(locale, 'more dropdown')}
                  >
                    {props.alternativeActionOptions.title}
                  </HoverableLink>
                }
                openComponent={<SvgChevronUpLoD />}
                closedComponent={<SvgChevronDownLoD />}
                minWidth={250}
              />
            </div>
          )} */}
          {/* {props.alternativeActionOptions.titleActions !== undefined && (
            <HoverableLink
              onClick={props.alternativeActionOptions.titleActions.dispatch}
              ariaLabel={props.alternativeActionOptions.titleActions.ariaLabelSource}
              colorSvgOnHover
              css={css`
                ${getFontCss(14, themeConstants.colors.cta, styleConstants.fontFamilies.medium)};
                display: flex;
                gap: 4px;
                justify-content: center;
                align-items: center;
                white-space: normal;
                padding-bottom: 5px;
              `}>
              <img
                src={props.alternativeActionOptions.titleActions.imageSrc}
                alt={''}
              />
              {props.alternativeActionOptions.title}
            </HoverableLink>
          )} */}
          {props.mainActionOptions !== undefined &&
            props.mainActionOptions.topActionOptions !== undefined && (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  height: 100%;
                `}
              >
                {props.mainActionOptions.topActionOptions[0] !== undefined && (
                  <Button
                    css={css`
                      ${getBreakpointDown(breakpointSizes.XSM)(css`
                        margin-top: 12px;
                        ${getFontCss(
                          12,
                          styleConstants.colors.white,
                          styleConstants.fontFamilies.medium,
                        )};
                        white-space: nowrap;
                        overflow: hidden;
                      `)}
                    `}
                    linkProps={{
                      to: props.mainActionOptions.topActionOptions[0].to || '',
                      ...(props.mainActionOptions.topActionOptions[0]
                        .ariaLabelSource !== undefined
                        ? {
                            ariaLabelSource:
                              props.mainActionOptions.topActionOptions[0]
                                .ariaLabelSource,
                          }
                        : {
                            ariaLabel:
                              props.mainActionOptions.topActionOptions[0]
                                .ariaLabel,
                          }),
                    }}
                    design={ButtonDesigns.designCta}
                    type={ButtonTypes.button}
                    text={props.mainActionOptions.topActionOptions[0].label}
                    grayed={props.mainActionOptions.topActionOptions[0].grayed}
                    testId="updateBtn"
                    onClick={(): void => {
                      props.titleElements
                        ?.canRequestVirtualCardUpdateForDetailTopnav &&
                        analytics?.track(
                          'virtual cards - open request virtual card update flyout - button press',
                          {
                            screen: '/virtualcards',
                            interaction: 'button press',
                            intent: 'open request virtual card update flyout',
                          },
                        )
                    }}
                  />
                )}
              </div>
            )}
        </div>
      </div>
    </>
  )
}

export default Topnav
